import { useState, useEffect, useRef, useCallback } from "react";

import { Mic, MicOff } from "lucide-react";
import FrequencyVisualizer from "./FrequencyVisualizer";
import { Button } from "@mui/material";
import { TORCH_SHOW_FIXED_EFFECTS } from "../../utils/defaultSettings";

const AudioAnalyzer = ({ onToneChange }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);
  const [microphone, setMicrophone] = useState(null);
  const [frequencies, setFrequencies] = useState(new Uint8Array(256));
  const [bpm, setBpm] = useState(0);
  const [intensity, setIntensity] = useState(0);
  const [activeTone, setActiveTone] = useState(null);

  // Simple timing tracking
  const [lastEffectChange, setLastEffectChange] = useState(0);

  const animationRef = useRef();
  const beatDetectionRef = useRef([]);
  const energyHistoryRef = useRef([]);

  const initializeAudio = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      const context = new AudioContext({
        sampleRate: 48000
      });
      const source = context.createMediaStreamSource(stream);
      const analyserNode = context.createAnalyser();

      analyserNode.fftSize = 2048;
      analyserNode.smoothingTimeConstant = 0.8;

      source.connect(analyserNode);

      setAudioContext(context);
      setAnalyser(analyserNode);
      setMicrophone(source);
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (microphone) {
      microphone.mediaStream.getTracks().forEach((track) => track.stop());
    }
    if (audioContext) {
      audioContext.close();
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    setIsRecording(false);
    setAudioContext(null);
    setAnalyser(null);
    setMicrophone(null);
    setActiveTone(null);
    setLastEffectChange(0);
  }, [microphone, audioContext]);

  const analyzeAudio = useCallback(() => {
    if (!analyser) return;

    // Get both frequency and time-domain data for better beat detection
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    const timeDomainData = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(frequencyData);
    analyser.getByteTimeDomainData(timeDomainData);
    setFrequencies(frequencyData);

    // Calculate intensity from frequency data
    const avgIntensity =
      frequencyData.reduce((sum, value) => sum + value, 0) /
      frequencyData.length;
    const currentIntensity = Math.round((avgIntensity / 255) * 100);
    setIntensity(currentIntensity);

    // Calculate energy from time-domain data for beat detection
    const energy =
      timeDomainData.reduce((sum, value) => {
        const normalized = (value - 128) / 128;
        return sum + normalized * normalized;
      }, 0) / timeDomainData.length;

    const normalizedEnergy = Math.sqrt(energy) * 100;

    // Adaptive beat detection threshold using rolling mean + k*std
    const history = energyHistoryRef.current;
    history.push(normalizedEnergy);
    if (history.length > 90) {
      energyHistoryRef.current = history.slice(-90);
    }
    const h = energyHistoryRef.current;
    const mean = h.reduce((s, v) => s + v, 0) / h.length;
    const variance =
      h.reduce((s, v) => s + (v - mean) * (v - mean), 0) / h.length;
    const std = Math.sqrt(variance);
    const k = 2.0; // sensitivity multiplier
    const minThresh = 0.8;
    const maxThresh = 5.0;
    const threshold = Math.max(minThresh, Math.min(maxThresh, mean + k * std));

    const now = Date.now();
    const isPeak = normalizedEnergy > threshold;

    // console.log(
    //   `🔍 Energy: ${normalizedEnergy.toFixed(2)}, mean: ${mean.toFixed(
    //     2
    //   )}, std: ${std.toFixed(2)}, thr: ${threshold.toFixed(2)}, Peak: ${isPeak}`
    // );

    // Only detect beats if there's been at least 200ms since last beat (300 BPM max)
    const lastBeat =
      beatDetectionRef.current[beatDetectionRef.current.length - 1] || 0;
    const timeSinceLastBeat = now - lastBeat;
    const canDetectBeat = timeSinceLastBeat > 200;

    if (isPeak && canDetectBeat) {
      beatDetectionRef.current.push(now);
      beatDetectionRef.current = beatDetectionRef.current.filter(
        (beat) => now - beat < 8000
      ); // Keep 8 seconds

      console.log(
        `🥁 Beat detected! Energy: ${normalizedEnergy.toFixed(
          1
        )}, Total beats: ${beatDetectionRef.current.length}`
      );

      if (beatDetectionRef.current.length >= 3) {
        // Reduced from 4 to 3
        const intervals = [];
        for (let i = 1; i < beatDetectionRef.current.length; i++) {
          intervals.push(
            beatDetectionRef.current[i] - beatDetectionRef.current[i - 1]
          );
        }

        // Less restrictive filtering for realistic BPM range
        const validIntervals = intervals.filter(
          (interval) => interval > 300 && interval < 1714
        ); // 35-200 BPM
        console.log(
          `🔍 BPM Debug: ${intervals.length} total intervals, ${validIntervals.length} valid intervals`
        );

        if (validIntervals.length >= 2) {
          // Reduced from 3 to 2
          const sortedIntervals = [...validIntervals].sort((a, b) => a - b);
          const medianInterval =
            sortedIntervals[Math.floor(sortedIntervals.length / 2)];
          const calculatedBpm = Math.round(60000 / medianInterval);

          console.log(
            `🎯 BPM Calculation: ${calculatedBpm} BPM (median: ${medianInterval}ms)`
          );

          if (calculatedBpm >= 35 && calculatedBpm <= 200) {
            // Lowered min from 40 to 35
            if (bpm === 0 || Math.abs(calculatedBpm - bpm) <= 35) {
              // Increased tolerance from 25 to 35
              setBpm(calculatedBpm);
              console.log(
                `✅ BPM Updated: ${calculatedBpm} (median: ${medianInterval}ms, ${validIntervals.length} intervals)`
              );
            } else {
              console.log(
                `❌ BPM rejected: change too large (${Math.abs(
                  calculatedBpm - bpm
                )} > 35)`
              );
            }
          } else {
            console.log(`❌ BPM out of range: ${calculatedBpm}`);
          }
        } else {
          console.log(
            `❌ Not enough valid intervals: ${validIntervals.length} < 2`
          );
        }
      } else {
        console.log(
          `❌ Not enough beats: ${beatDetectionRef.current.length} < 3`
        );
      }
    }

    // Generate tones with improved variety logic
    generateTone(currentIntensity, bpm);
    animationRef.current = requestAnimationFrame(analyzeAudio);
  }, [analyser, activeTone, lastEffectChange, bpm]);

  const generateTone = useCallback(
    (currentIntensity, currentBpm) => {
      const now = Date.now();
      const timeSinceLastChange = now - lastEffectChange;

      // ENFORCE 8-second maximum for any effect (reduced for more variety)
      const maxDuration = 8000;
      const forceChange = timeSinceLastChange >= maxDuration;

      // Enhanced effect selection with better Solid Light integration
      let targetFrequency = null;

      if (currentIntensity < 3) {
        targetFrequency = null; // Silence
      } else {
        // Create variety by using multiple factors: intensity, time, and BPM
        const timeBasedVariation = Math.floor((now / 3000) % 6); // Changes every 3 seconds, 6 slots
        const intensityLevel =
          currentIntensity >= 24
            ? 3
            : currentIntensity >= 20
            ? 2
            : currentIntensity >= 7
            ? 1
            : 0;

        // More balanced selection with fast strobe only at high intensity
        if (intensityLevel === 3) {
          // High intensity (27+)
          // Fast strobe only appears at high intensity
          const highEffects = [19000, 16000, 15000, 19000]; // Fast strobe, Medium strobe, Solid Light
          if (
            currentBpm >= 90 &&
            currentBpm <= 110 &&
            timeBasedVariation === 1
          ) {
            targetFrequency = 15000; // Solid Light for moderate BPM, but only in specific time slot
          } else {
            targetFrequency =
              highEffects[timeBasedVariation % highEffects.length];
          }
        } else if (intensityLevel === 2) {
          // Medium intensity (20-26) - includes 32
          // Medium strobe dominates this range, with Solid Light for 32+ intensity
          const mediumEffects =
            currentIntensity >= 32
              ? [15000, 16000, 15000]
              : [16000, 18000, 16000]; // Solid Light prominent at 32+, Medium strobe otherwise
          targetFrequency =
            mediumEffects[timeBasedVariation % mediumEffects.length];
        } else if (intensityLevel === 1) {
          // Low-medium intensity (7-19)
          // Include Solid Light more at lower intensity
          const lowMedEffects = [18000, 15000, 18000]; // Solid Light appears once every 3
          targetFrequency =
            lowMedEffects[timeBasedVariation % lowMedEffects.length];
        } else {
          // Very low intensity
          targetFrequency = timeBasedVariation % 3 === 0 ? 15000 : 18000; // Solid Light every third time
        }

        // Remove forced Solid Light at medium intensity to prevent conflicts

        // BPM influence (when available) - more selective to preserve medium strobe
        if (currentBpm > 0 && targetFrequency !== 15000) {
          if (currentBpm > 160 && intensityLevel >= 2) {
            // Only override for very fast songs
            targetFrequency = 19000; // Fast strobe for very fast songs
          } else if (currentBpm < 60 && intensityLevel <= 2) {
            // Only override for very slow songs
            targetFrequency = 18000; // Slow pulse for very slow songs
          }
          // Let medium strobe (20000Hz) play for normal BPM ranges (60-160)
        }
      }

      // Force variety - if same effect for too long, switch to different one
      if (activeTone === targetFrequency && timeSinceLastChange > 7000) {
        const allEffects = [15000, 16000, 18000, 19000];
        const otherEffects = allEffects.filter((freq) => freq !== activeTone);
        targetFrequency =
          otherEffects[Math.floor(Math.random() * otherEffects.length)];
        console.log(`🔀 Forced variety change to ${targetFrequency}Hz`);
      }

      // Switching logic
      let shouldChange = false;

      if (activeTone === null && targetFrequency !== null) {
        shouldChange = true;
        console.log(`🎬 Starting: ${targetFrequency}Hz`);
      } else if (activeTone !== null && targetFrequency === null) {
        shouldChange = true;
        console.log(`🔇 Stopping for silence`);
      } else if (forceChange) {
        shouldChange = true;
        console.log(
          `⏰ FORCE CHANGE after ${(timeSinceLastChange / 1000).toFixed(1)}s`
        );
      } else if (
        targetFrequency !== activeTone &&
        timeSinceLastChange >= 2000
      ) {
        shouldChange = true;
        console.log(
          `✅ Normal change after ${(timeSinceLastChange / 1000).toFixed(1)}s`
        );
      }

      if (shouldChange) {
        if (targetFrequency !== activeTone) {
          if (activeTone !== null) {
            //toneGeneratorRef.current.stop();
          }
          if (targetFrequency !== null) {
            //toneGeneratorRef.current.play(targetFrequency);
            console.log(
              `🎵 Now playing: ${targetFrequency}Hz (${getToneDescription(
                targetFrequency
              )}) - I:${currentIntensity}%`
            );
          }
        }

        setActiveTone(targetFrequency);
        onToneChange(targetFrequency);
        setLastEffectChange(now);
      }
    },
    [activeTone, lastEffectChange]
  );

  const getToneDescription = (frequency) => {
    const mapping = TORCH_SHOW_FIXED_EFFECTS[frequency];
    return mapping ? mapping.name : "Unknown";
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      initializeAudio();
    }
  };

  useEffect(() => {
    if (isRecording && analyser) {
      analyzeAudio();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRecording, analyser, analyzeAudio]);

  return (
    <div className="bg-background p-4">
      <div className="mx-auto max-w-4xl space-y-2">
        <div className="p-0 bg-transparent">
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <span className="text-muted-foreground">
                Real-time audio analysis
              </span>
              <Button
                onClick={handleToggleRecording}
                variant="contained"
                startIcon={isRecording ? <MicOff /> : <Mic />}
                size="lg"
                className="w-48"
                sx={{ color: "white" }}
              >
                {isRecording ? <>Stop Listening</> : <>Start Listening</>}
              </Button>
            </div>

            {isRecording && (
              <>
                <FrequencyVisualizer
                  frequencies={frequencies}
                  isActive={isRecording}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold">{intensity}%</div>
                    <div className="text-sm text-muted-foreground">
                      Intensity
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold">{bpm}</div>
                    <div className="text-sm text-muted-foreground">BPM</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold">
                      {activeTone ? getToneDescription(activeTone) : "Silence"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Active Effect
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AudioAnalyzer;
